/**
 * QuimeraX Threat Report Feed (TRF) — shared types, helpers and safe Markdown.
 *
 * Mirrors the API contract in hackingclub-api.md / hackingclub-frontend.md.
 * The feed's entire state is two values, `q` and `page`; a filter is just a
 * clause appended to `q`. Dates are always formatted in UTC.
 */
import MarkdownIt from "markdown-it";

// ---------------------------------------------------------------------------
// Types — one interface per API response shape.
// ---------------------------------------------------------------------------

export interface IndicatorGroup {
  label: string;
  field: string;
  values: string[];
}

export interface CaseListItem {
  uuid: string;
  title: string;
  summary: string;
  latest_at: string | null;
  first_at: string | null;
  update_count: number;
  source_count: number;
  latest_source_host: string | null;
  latest_source_url: string | null;
  criticity: string | null;
}

export interface FacetBucket {
  key: string;
  count: number;
}

export interface Facet {
  field: string;
  label: string;
  buckets: FacetBucket[];
}

export interface CaseListResult {
  cases: CaseListItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  start_index: number;
  end_index: number;
  has_prev: boolean;
  has_next: boolean;
  facets: Facet[];
  query: string;
  error: string | null;
  challenge_required?: boolean;
}

export interface CaseStats {
  total_docs: number;
  new_info_count: number;
  no_update_count: number;
  source_count: number;
  first_published_at: string | null;
  last_published_at: string | null;
  first_collected_at: string | null;
  last_collected_at: string | null;
}

export interface TimelineEntry {
  title: string;
  news_url: string;
  feed_url: string;
  source_host: string;
  summary: string;
  new_context: string | null;
  summary_with_context: string | null;
  published_at: string | null;
  collected_at: string | null;
  indicators: IndicatorGroup[];
}

export interface NoUpdateSource {
  title: string;
  news_url: string;
  source_host: string;
  collected_at: string | null;
  published_at: string | null;
}

export interface CaseDetail {
  uuid: string;
  title: string;
  latest_summary: string | null;
  criticity: string | null;
  victim_sectors: string[];
  stats: CaseStats;
  indicators: IndicatorGroup[];
  timeline: TimelineEntry[];
  no_update_sources: NoUpdateSource[];
}

export interface Meta {
  searchable_fields: string[];
  indicator_groups: { field: string; label: string }[];
}

export interface UpdateItem {
  uuid: string;
  title: string;
  criticity: string | null;
  new_context: string | null;
  is_first: boolean;
  collected_at: string;
  source_host: string | null;
}

export interface DashboardBucket {
  key: string;
  count: number;
}

export interface Dashboard {
  window_days: number;
  total_cases: number;
  critical_count: number;
  updated_last_7d: number;
  criticity: DashboardBucket[];
  countries: DashboardBucket[];
  top_threat_actors: DashboardBucket[];
  top_cves: DashboardBucket[];
  top_victim_sectors: DashboardBucket[];
  top_malware: DashboardBucket[];
  activity: DashboardBucket[];
}

// ---------------------------------------------------------------------------
// Dates — always formatted in UTC (threat intel is compared across timezones).
// ---------------------------------------------------------------------------

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

export function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : DATE_FMT.format(d);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${DATE_FMT.format(d)} ${TIME_FMT.format(d)} UTC`;
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

/** Freshness flag — the reference marks a case "updated" within 48h. */
export function isRecent(
  value: string | null | undefined,
  withinHours = 48
): boolean {
  if (!value) return false;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return false;
  return Date.now() - then <= withinHours * 3600 * 1000;
}

// ---------------------------------------------------------------------------
// Criticity — nullable and free-form on the wire; normalise before use.
// ---------------------------------------------------------------------------

export const CRITICITY_LEVELS = ["low", "medium", "high", "critical"] as const;
export type Criticity = (typeof CRITICITY_LEVELS)[number];

/** Returns a known level in lowercase, or null (render no severity indicator). */
export function normalizeCriticity(value: string | null | undefined): Criticity | null {
  const level = (value ?? "").trim().toLowerCase();
  return (CRITICITY_LEVELS as readonly string[]).includes(level)
    ? (level as Criticity)
    : null;
}

/** Bootstrap contextual colour for a criticity level. */
export function criticityColor(value: string | null | undefined): string {
  switch (normalizeCriticity(value)) {
    case "critical":
      return "danger";
    case "high":
      return "warning";
    case "medium":
      return "primary";
    case "low":
      return "secondary";
    default:
      return "secondary";
  }
}

// ---------------------------------------------------------------------------
// Filtering — a filter is a clause appended to `q`, joined by " AND ".
// ---------------------------------------------------------------------------

/** A query_string clause targeting one entity value, e.g. cves:"CVE-2026-1". */
export function entityQueryString(field: string, value: string): string {
  return `${field}:"${value}"`;
}

/** Decompose q into its AND-separated clauses. */
export function clausesOf(q: string): string[] {
  return q
    .split(/\s+AND\s+/i)
    .map((c) => c.trim())
    .filter(Boolean);
}

/** Recompose a clause list into a q string. */
export function joinClauses(list: string[]): string {
  return list.join(" AND ");
}

/** The active criticity level within q, if any (single-select). */
export function activeCriticity(q: string): Criticity | null {
  for (const c of clausesOf(q)) {
    const m = c.match(/^criticity:\s*"?([a-z]+)"?$/i);
    if (m) return normalizeCriticity(m[1]);
  }
  return null;
}

/** External enrichment URL for CVEs (NVD) and ATT&CK techniques (MITRE). */
export function externalLink(field: string, value: string): string | null {
  if (field === "cves") {
    const m = value.toUpperCase().match(/CVE-\d{4}-\d{4,}/);
    if (m) return `https://nvd.nist.gov/vuln/detail/${m[0]}`;
  }
  if (field === "ttps_attack") {
    const m = value.toUpperCase().match(/T(\d{4})(?:\.(\d{3}))?/);
    if (m) {
      return m[2]
        ? `https://attack.mitre.org/techniques/T${m[1]}/${m[2]}/`
        : `https://attack.mitre.org/techniques/T${m[1]}/`;
    }
  }
  return null;
}

/** Count of indicator values across a group list. */
export function indicatorCount(groups: IndicatorGroup[]): number {
  return groups.reduce((n, g) => n + g.values.length, 0);
}

// ---------------------------------------------------------------------------
// Markdown — LLM-authored, third-party sourced. Raw HTML MUST stay disabled
// (XSS). External links are forced to open safely in a new tab.
// ---------------------------------------------------------------------------

const md = new MarkdownIt("commonmark", {
  html: false,
  linkify: true,
  breaks: false,
})
  .enable("table")
  .enable("strikethrough");

const defaultLinkOpen =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const href = tokens[idx].attrGet("href") || "";
  if (href.startsWith("http://") || href.startsWith("https://")) {
    tokens[idx].attrSet("target", "_blank");
    tokens[idx].attrSet("rel", "noopener noreferrer");
  }
  return defaultLinkOpen(tokens, idx, options, env, self);
};

export const renderMarkdown = (text?: string | null): string =>
  text ? md.render(text) : "";
